import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Inbox } from 'lucide-react';

import type { ForecastAnomaliesPayload, ForecastAnomaly } from '../types/forecast.types';
import { EMPTY_ENTITY_SCOPE_FILTERS, type EntityScopeFilters } from './ConsensusScopeFilters';
import { HelpTip } from './HelpTip';

function matchesEntityFilters(alert: ForecastAnomaly, filters: EntityScopeFilters): boolean {
  if (!filters.family_codes.length) return true;
  if (alert.scope_type === 'family' && alert.scope_code) {
    return filters.family_codes.includes(alert.scope_code);
  }
  return true;
}

interface Props {
  data: ForecastAnomaliesPayload | undefined;
  loading?: boolean;
  entityFilters?: EntityScopeFilters;
  selectedAlertId?: number | null;
  onSelectAlert?: (alert: ForecastAnomaly | null) => void;
  onRequestApprove?: (alert: ForecastAnomaly) => void;
  onRequestDismiss?: (alert: ForecastAnomaly) => void;
  actionBusyId?: number | null;
}

export function ForecastWorkInbox({
  data,
  loading,
  entityFilters,
  selectedAlertId,
  onSelectAlert,
  onRequestApprove,
  onRequestDismiss,
  actionBusyId,
}: Props) {
  const { t } = useTranslation();
  const filters = entityFilters ?? EMPTY_ENTITY_SCOPE_FILTERS;

  const active = (data?.active ?? []).filter((row) => matchesEntityFilters(row, filters));

  return (
    <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 bg-gray-50/80 px-4 py-3">
        <div className="flex items-center gap-2">
          <Inbox className="h-4 w-4 text-stocker-primary" />
          <div>
            <div className="flex items-center gap-1">
              <h2 className="text-sm font-semibold text-gray-900">{t('forecastPage.workInbox.title')}</h2>
              <HelpTip>{t('forecastPage.help.inbox')}</HelpTip>
            </div>
            <p className="text-xs text-gray-500">{t('forecastPage.workInbox.subtitle')}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {data?.summary ? (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900">
              {t('forecastPage.workInbox.pending', { count: active.length })}
            </span>
          ) : null}
          <Link
            to="/demand-planning/forecast/anomalies"
            className="text-xs font-medium text-stocker-primary hover:underline"
          >
            {t('forecastPage.viewAllAnomalies')}
          </Link>
        </div>
      </div>

      <div className="px-4 py-3">
        {loading && !data ? (
          <p className="py-4 text-sm text-gray-500">{t('common.loading')}</p>
        ) : active.length === 0 ? (
          <p className="py-4 text-sm text-gray-500">{t('forecastPage.workInbox.empty')}</p>
        ) : (
          <div className="max-h-64 overflow-y-auto space-y-2">
            {active.map((alert) => {
              const selected = selectedAlertId === alert.id;
              return (
                <div
                  key={alert.id}
                  className={`rounded-md border px-3 py-2 text-sm ${
                    selected
                      ? 'border-stocker-primary bg-sky-50'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <button
                    type="button"
                    className="w-full text-left"
                    onClick={() => onSelectAlert?.(selected ? null : alert)}
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-gray-900">{alert.title}</span>
                      <span className="text-[10px] uppercase text-gray-400">{alert.severity}</span>
                    </div>
                    <p className="mt-0.5 text-xs text-gray-600 line-clamp-2">{alert.message}</p>
                    <p className="mt-1 text-[10px] text-gray-400">
                      {alert.scope_type}: {alert.scope_code ?? '—'}
                    </p>
                  </button>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {alert.supports_apply_recommendation ? (
                      <button
                        type="button"
                        disabled={actionBusyId === alert.id}
                        className="rounded bg-stocker-primary px-2 py-1 text-xs text-white disabled:opacity-50"
                        onClick={() => onRequestApprove?.(alert)}
                      >
                        {t('forecastPage.workInbox.approve')}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      disabled={actionBusyId === alert.id}
                      className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 disabled:opacity-50"
                      onClick={() => onRequestDismiss?.(alert)}
                    >
                      {t('forecastPage.workInbox.dismiss')}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
